import React from 'react';
import { useModal } from 'react-modal-hook';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, Icon, Text } from 'components';
import { useFeatureFlags } from 'contexts/featureFlags';
import { theme, toRem } from 'design';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { FeatureFlags, PermissionType } from 'types';
import { hasSitePermission } from 'utils';
import { Title } from '../../../styles';
import { AIRulesModal } from './AIRulesModal';

interface ISuggestRulesProps {}

const Layout = styled.div`
  flex-direction: column;
  align-items: flex-start;
  padding: ${toRem(theme.space.xl)};
  gap: ${toRem(theme.space.lg)};
  background: ${theme.color.primary[25]};
  border: ${toRem(1)} solid ${theme.color.primary[500]};
  border-radius: ${toRem(8)};
  align-self: stretch;
  flex-grow: 0;
  margin-top: ${toRem(theme.space.lg)};

  p {
    color: ${theme.color.neutral[600]};
  }
`;

const StyledButton = styled(Button)`
  width: ${toRem(200)} !important;
`;

const StyledIcon = styled(Icon)`
  position: relative;
  margin-right: ${toRem(theme.space.sm)};
  fill: ${theme.color.primary[600]};
  top: ${toRem(3)};
`;

export const SuggestRules: React.FC<ISuggestRulesProps> = () => {
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const suggestRulesPermission = hasSitePermission(currentSite?.permissions, PermissionType.GENERATE_CALCULATOR_RULES);
  const suggestSalesCategoriesGroups = hasSitePermission(currentSite?.permissions, PermissionType.GENERATE_SALES_CATEGORIES_GROUPS);
  const tipCalculatorSuggestRulesEnabled = useFeatureFlags()[FeatureFlags.TIP_CALCULATOR_AI_RULES_SUGGEST]?.enabled;

  // Do not display if we do not have permissions or the feature flag is not enabled
  if (!suggestRulesPermission || !suggestSalesCategoriesGroups || !tipCalculatorSuggestRulesEnabled) return <></>;

  const [showRulesLoader, hideRulesLoader] = useModal(() => <AIRulesModal siteId={currentSite.id} onClose={hideRulesLoader} />, [currentSite]);

  return (
    <Layout data-cy="ai-rules-suggestion">
      <Title fontWeight="medium">
        <StyledIcon icon="Wizard" />
        Tip Calculator Rules & Sales Group Generator
      </Title>
      <Text size="xsmall">
        Automatically generate tip calculator rules using your point of sale data. Let AI analyze your sales, roles, and tip patterns to suggest fair, consistent rule sets — eliminating guesswork and accelerating setup.
      </Text>
      <StyledButton appearance="primary" size="small" label="Suggest Tip Calculator Rules" data-cy="ai-rules-button" onClick={showRulesLoader} />
    </Layout>
  );
};
